/**
 * Catálogo de permissões (spec §5.2) e papéis sugeridos (spec §4.3).
 * Fonte da verdade: é daqui que o seed popula a tabela `permissions`.
 */

export const PERMISSIONS = {
  // Eventos
  EVENTS_CREATE: 'events.create',
  EVENTS_UPDATE: 'events.update',
  EVENTS_DELETE: 'events.delete',
  EVENTS_READ: 'events.read',

  // Participantes
  PARTICIPANTS_CREATE: 'participants.create',
  PARTICIPANTS_UPDATE: 'participants.update',
  PARTICIPANTS_DELETE: 'participants.delete',
  PARTICIPANTS_IMPORT: 'participants.import',
  PARTICIPANTS_READ: 'participants.read',
  /** Ver telefone/e-mail sem máscara. Separada por exigência de LGPD. */
  PARTICIPANTS_READ_PII: 'participants.read_pii',

  // Jornadas
  JOURNEYS_START: 'journeys.start',
  JOURNEYS_SCHEDULE: 'journeys.schedule',
  JOURNEYS_PAUSE: 'journeys.pause',
  JOURNEYS_CANCEL: 'journeys.cancel',

  // Conversas
  CONVERSATIONS_READ: 'conversations.read',
  CONVERSATIONS_TAKEOVER: 'conversations.takeover',
  CONVERSATIONS_SEND: 'conversations.send',

  // Templates
  TEMPLATES_MANAGE: 'templates.manage',
  TEMPLATES_SUBMIT: 'templates.submit',

  // Matchmaking
  MATCHING_RULES_MANAGE: 'matching.rules_manage',
  RECOMMENDATIONS_READ: 'recommendations.read',
  RECOMMENDATIONS_APPROVE: 'recommendations.approve',
  RECOMMENDATIONS_SEND: 'recommendations.send',

  // Dashboards e relatórios
  DASHBOARDS_READ: 'dashboards.read',
  REPORTS_EXPORT: 'reports.export',

  // Administração da organização
  USERS_MANAGE: 'users.manage',
  SETTINGS_MANAGE: 'settings.manage',
  AI_SETTINGS_MANAGE: 'ai.settings_manage',
  INTEGRATIONS_MANAGE: 'integrations.manage',
  AUDIT_READ: 'audit.read',
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export const ALL_PERMISSIONS = Object.values(PERMISSIONS) as Permission[];

export const ROLE_SLUGS = {
  ORG_ADMIN: 'admin-organizacao',
  EVENT_MANAGER: 'gestor-evento',
  OPERATOR: 'operador',
  ANALYST: 'analista',
  VIEWER: 'visualizador',
} as const;

export type RoleSlug = (typeof ROLE_SLUGS)[keyof typeof ROLE_SLUGS];

/** Permissões padrão por papel — ponto de partida, editável por organização. */
export const DEFAULT_ROLE_PERMISSIONS: Record<RoleSlug, Permission[]> = {
  [ROLE_SLUGS.ORG_ADMIN]: ALL_PERMISSIONS,

  [ROLE_SLUGS.EVENT_MANAGER]: [
    PERMISSIONS.EVENTS_CREATE,
    PERMISSIONS.EVENTS_UPDATE,
    PERMISSIONS.EVENTS_READ,
    PERMISSIONS.PARTICIPANTS_CREATE,
    PERMISSIONS.PARTICIPANTS_UPDATE,
    PERMISSIONS.PARTICIPANTS_IMPORT,
    PERMISSIONS.PARTICIPANTS_READ,
    PERMISSIONS.PARTICIPANTS_READ_PII,
    PERMISSIONS.JOURNEYS_START,
    PERMISSIONS.JOURNEYS_SCHEDULE,
    PERMISSIONS.JOURNEYS_PAUSE,
    PERMISSIONS.JOURNEYS_CANCEL,
    PERMISSIONS.CONVERSATIONS_READ,
    PERMISSIONS.CONVERSATIONS_TAKEOVER,
    PERMISSIONS.CONVERSATIONS_SEND,
    PERMISSIONS.TEMPLATES_MANAGE,
    PERMISSIONS.MATCHING_RULES_MANAGE,
    PERMISSIONS.RECOMMENDATIONS_READ,
    PERMISSIONS.RECOMMENDATIONS_APPROVE,
    PERMISSIONS.RECOMMENDATIONS_SEND,
    PERMISSIONS.DASHBOARDS_READ,
    PERMISSIONS.REPORTS_EXPORT,
  ],

  [ROLE_SLUGS.OPERATOR]: [
    PERMISSIONS.EVENTS_READ,
    PERMISSIONS.PARTICIPANTS_CREATE,
    PERMISSIONS.PARTICIPANTS_UPDATE,
    PERMISSIONS.PARTICIPANTS_IMPORT,
    PERMISSIONS.PARTICIPANTS_READ,
    PERMISSIONS.PARTICIPANTS_READ_PII,
    PERMISSIONS.JOURNEYS_START,
    PERMISSIONS.JOURNEYS_SCHEDULE,
    PERMISSIONS.CONVERSATIONS_READ,
    PERMISSIONS.CONVERSATIONS_TAKEOVER,
    PERMISSIONS.CONVERSATIONS_SEND,
    PERMISSIONS.DASHBOARDS_READ,
  ],

  [ROLE_SLUGS.ANALYST]: [
    PERMISSIONS.EVENTS_READ,
    PERMISSIONS.PARTICIPANTS_READ,
    PERMISSIONS.CONVERSATIONS_READ,
    PERMISSIONS.RECOMMENDATIONS_READ,
    PERMISSIONS.DASHBOARDS_READ,
    PERMISSIONS.REPORTS_EXPORT,
  ],

  [ROLE_SLUGS.VIEWER]: [
    PERMISSIONS.EVENTS_READ,
    PERMISSIONS.PARTICIPANTS_READ,
    PERMISSIONS.DASHBOARDS_READ,
  ],
};
